import {
  Badge,
  Button,
  Checkbox,
  Divider,
  Dropdown,
  IconButton,
  Icons,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Switch,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useAtom } from 'jotai/index';
import { useEffect, useState } from 'react';
import { AssignedToFilter } from '../../../../api/assignedToFilter.ts';
import {
  patientAssignedToAtom,
  patientSearchTextAtom,
  patientTableActiveOnly,
  PatientTableFilter,
  patientTableFiltersAtom,
  PatientTableView,
  patientTableViewAtom,
} from '../../../../state/patientSearchAtoms.ts';
import {
  getFilterOptionsForPatientTableView,
  getPatientTableFilterName,
  getPatientTableViewName,
} from '../../../../utils/patientSearch/patientTableFilter.ts';
import { PatientTableAssignedToFilter } from './PatientTableAssignedToFilter.tsx';

export function PatientTableSearch({
  tableViews = Object.values(PatientTableView),
  selectedTable,
}: {
  tableViews?: PatientTableView[];
  selectedTable: PatientTableView;
}) {
  const [, setSelectedTable] = useAtom(patientTableViewAtom);
  const [, setSearchText] = useAtom(patientSearchTextAtom);
  const [search, setSearch] = useState('');
  const [selectedTableFilters, setSelectedTableFilters] = useAtom(patientTableFiltersAtom);
  const [activeOnly, setActiveOnly] = useAtom(patientTableActiveOnly);
  const [assignedTo] = useAtom(patientAssignedToAtom);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setSearchText(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search, setSearchText]);

  const showAssignedToFilter = selectedTable !== PatientTableView.SCREENING;
  const hasActiveAssignedToFilter = showAssignedToFilter && assignedTo !== AssignedToFilter.ALL;
  const hasActiveFilter =
    getFilterOptionsForPatientTableView(selectedTable).some((option) => selectedTableFilters.has(option)) ||
    hasActiveAssignedToFilter;
  return (
    <Stack direction={'row'} justifyContent={'space-between'}>
      <Stack direction={'row'} gap={2}>
        {tableViews.length > 1 && (
          <ToggleButtonGroup value={selectedTable}>
            {tableViews.map((filter) => (
              <Button key={filter} value={filter} onClick={() => setSelectedTable(filter)}>
                {getPatientTableViewName(filter)}
              </Button>
            ))}
          </ToggleButtonGroup>
        )}
        <Switch endDecorator={'Erledigte anzeigen'} checked={!activeOnly} onClick={() => setActiveOnly(!activeOnly)} />
      </Stack>
      <Stack direction={'row'} gap={2}>
        <Input
          placeholder={'Patienten suchen'}
          endDecorator={<Icons.Search />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Dropdown
          open={isMenuOpen}
          onOpenChange={(event) => event?.type !== 'click' && setIsMenuOpen((prevState) => !prevState)}>
          <Badge invisible={!hasActiveFilter}>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{ root: { variant: 'soft', color: 'neutral' } }}
              onClick={() => setIsMenuOpen((prevState) => !prevState)}>
              <Icons.FilterList />
            </MenuButton>
          </Badge>
          <Menu placement={'bottom-end'}>
            <MenuItem>
              <Typography level={'title-md'}>{getPatientTableViewName(selectedTable)}</Typography>
            </MenuItem>
            <Divider />
            <FilterOptions
              selectedTable={selectedTable}
              selectedTableFilters={selectedTableFilters}
              onSelect={(filter) =>
                setSelectedTableFilters((prevFilters) => {
                  const updatedFilters = new Set(prevFilters);
                  if (updatedFilters.has(filter)) {
                    updatedFilters.delete(filter);
                  } else {
                    updatedFilters.add(filter);
                  }
                  return updatedFilters;
                })
              }
            />
            {showAssignedToFilter && (
              <>
                <Divider />
                <PatientTableAssignedToFilter />
              </>
            )}
          </Menu>
        </Dropdown>
      </Stack>
    </Stack>
  );
}

function FilterOptions({
  selectedTable,
  selectedTableFilters,
  onSelect,
}: {
  selectedTable: PatientTableView;
  selectedTableFilters: Set<PatientTableFilter>;
  onSelect: (filter: PatientTableFilter) => void;
}) {
  const filtersForTable = getFilterOptionsForPatientTableView(selectedTable);

  return (
    <>
      {filtersForTable.map((filter) => (
        <MenuItem
          key={filter}
          onClick={() => {
            onSelect(filter);
          }}>
          <Typography startDecorator={<Checkbox checked={selectedTableFilters.has(filter) ?? false} />}>
            {getPatientTableFilterName(filter)}
          </Typography>
        </MenuItem>
      ))}
    </>
  );
}
